import React from 'react';
import { Link } from 'react-router-dom';
import { Grid, Responsive } from 'vendors/semantic-ui';
import isEqual from 'lodash.isequal';

import { configUI } from 'config';
import { ImagesViewer } from 'modules/shared-ui-advanced/components';

import { readCMSValue } from 'modules/shared-cms/utils';
import { getBrandUrl } from '../../../utils';
import { Product as ProductModel } from '../../../models';

import { CatalogueAdvantage, SignImage, BrandImage } from '../..';

import { ProductDetails, ProductDiscoveryCarousel, ProductAddToList } from './components';
import { ProductProps } from './product-type';

import enhancer from './product-enhancer';

import styles from './product-component.module.css';

const buidlLabels = (product: ProductModel): JSX.Element => {
    if (product && product.name) {
        return (
            <React.Fragment>
                <div className={styles.productLabels}>
                    {product.brand && (
                        <div className={styles.productBrand}>
                            <Link to={getBrandUrl(product.brand)}>{product.brand.name}</Link>
                        </div>
                    )}
                    <div className={styles.productName}>
                        <span>{product.name}</span>
                    </div>
                </div>
            </React.Fragment>
        );
    }
    return <React.Fragment></React.Fragment>;
};

export const InnerProduct: React.FC<ProductProps> = React.memo(
    (props: ProductProps) => {
        const {
            name,
            brand,
            variants = [],
            images,
            currentElements,
            updateQuantities,
            isAuthenticated,
            brandDataFromBo: { link: brandUrlFromBO },
            ...rest
        } = props;

        const signData = rest && rest.cmsSignData && rest.cmsSignData.length > 0 ? rest.cmsSignData[0].data : undefined;
        const cmsBrandUrl =
            rest && rest.cmsBrandCorner && rest.cmsBrandCorner.length > 0
                ? readCMSValue(rest.cmsBrandCorner[0].data.link)
                : undefined;
        const brandCorner =
            rest && rest.cmsBrandCorner && rest.cmsBrandCorner.length > 0 ? rest.cmsBrandCorner[0].data : undefined;

        return (
            props && (
                <React.Fragment>
                    <div className={styles.container}>
                        <div className={styles.section}>
                            <Grid className={styles.grid}>
                                <Grid.Column mobile={16} tablet={8} computer={8}>
                                    <ImagesViewer
                                        name={name}
                                        brandName={brand ? brand.name : ''}
                                        images={images}
                                        loaderClassName={styles.desktopImageLoader}
                                        useBox
                                    />
                                </Grid.Column>
                                <Grid.Column mobile={16} tablet={8} computer={8}>
                                    <div className={styles.column}>
                                        {buidlLabels(props)}
                                        <div className={styles.priceContainer}>
                                            <ProductAddToList
                                                variants={variants}
                                                currentElements={currentElements}
                                                updateQuantities={updateQuantities}
                                                isAuthenticated={isAuthenticated}
                                            />
                                        </div>
                                        <div className={styles.advantageContainer}>
                                            <CatalogueAdvantage
                                                signId={brand ? brand.signId : ''}
                                                {...rest}
                                                containerClassName={
                                                    signData ? styles.innerAdvantageContainerWithSignLogo : undefined
                                                }
                                                secondaryTitle={
                                                    signData ? (
                                                        <Responsive minWidth={configUI.responsive.computer.minWidth}>
                                                            <SignImage {...signData} name={signData.pageTitle} />
                                                        </Responsive>
                                                    ) : (
                                                        undefined
                                                    )
                                                }
                                            />
                                        </div>
                                    </div>
                                </Grid.Column>
                            </Grid>
                        </div>
                        <div className={styles.brandLogoContainer}>
                            <BrandImage
                                {...brand}
                                link={cmsBrandUrl || brandUrlFromBO || ''}
                                cmsLogo={brandCorner ? brandCorner.picture : undefined}
                            />
                            {signData && signData.logo && signData.logo !== null && (
                                <Responsive maxWidth={configUI.responsive.tablet.maxWidth}>
                                    <div className={styles.mobileSignContainer}>
                                        <SignImage {...signData} name={signData.pageTitle} />
                                    </div>
                                </Responsive>
                            )}
                        </div>
                    </div>
                    <ProductDetails {...props} />
                    <ProductDiscoveryCarousel {...props} />
                </React.Fragment>
            )
        );
    },
    (prevProps, nextProps) => {
        return isEqual(prevProps, nextProps);
    },
);

export const Product = enhancer(InnerProduct);
